import { APP_NAME, APP_TAGLINE, BRAND_LOGO, BRAND_LOGO_WHITE, BRAND_SKY } from "@/lib/brand";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiErrorDetail, api } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const sendForgot = async () => {
    const target = (forgotEmail || email).trim();
    if (!target) { toast.error("Enter your email first"); return; }
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: target });
      toast.success("If the account exists, a reset link has been sent to your email.");
      setForgotOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setForgotBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
        style={{
          backgroundImage:
            `linear-gradient(135deg, rgba(37,99,235,0.88), rgba(59,130,246,0.42)), url(${BRAND_SKY})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 w-fit">
          <img src={BRAND_LOGO_WHITE} alt={APP_NAME} className="h-[52px] w-auto object-contain" />
        </div>

        <div className="relative z-10 fade-in-up">
          <p className="overline text-white/80">Pre-order project orchestration</p>
          <h1 className="mt-4 text-4xl xl:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-md">
            {APP_TAGLINE}
          </h1>
          <p className="mt-5 text-white/85 max-w-md leading-relaxed">
            Everything that needs your attention — projects, revisions, workstreams and ERP handover — clearly in one place, from brief to delivery.
          </p>
        </div>

        <div className="relative z-10 text-xs text-white/70">© {new Date().getFullYear()} {APP_NAME} · {APP_TAGLINE}</div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <img src={BRAND_LOGO} alt={APP_NAME} className="h-8 w-auto object-contain" />
          </div>

          <p className="overline">Sign in</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Welcome back</h2>
          <p className="mt-2 text-sm text-slate-500">
            Sign in with your work account.
          </p>

          <Card className="mt-6 p-6 border-slate-200 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-xs font-medium text-slate-600">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="login-email-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-medium text-slate-600">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="mt-1"
                />
                <div className="mt-1.5 text-right">
                  <button type="button" onClick={() => { setForgotOpen((v) => !v); setForgotEmail(email); }} className="text-xs text-[#2563EB] hover:underline" data-testid="login-forgot-link">Forgot password?</button>
                </div>
              </div>
              {forgotOpen && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="login-forgot-panel">
                  <Label className="text-xs font-medium text-slate-600">Your account email</Label>
                  <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@company.com" data-testid="login-forgot-email" />
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" onClick={sendForgot} disabled={forgotBusy} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="login-forgot-send">{forgotBusy ? "Sending…" : "Send reset link"}</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setForgotOpen(false)}>Cancel</Button>
                  </div>
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] h-10"
                data-testid="login-submit-button"
              >
                {submitting ? "Signing in…" : "Sign in"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs text-slate-400">OR</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogle}
              className="w-full h-10 border-slate-300"
              data-testid="login-google-button"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1A6.63 6.63 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A10.99 10.99 0 0 0 1 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
              Continue with Google
            </Button>
          </Card>

          <div className="mt-6 text-center text-sm text-slate-500">
            Don&apos;t have an account?{" "}
            <a href="/signup" className="font-semibold text-[#2563EB] hover:underline" data-testid="login-signup-link">Create an account</a>
          </div>

          <div className="mt-8 pt-5 border-t border-slate-200 text-xs text-slate-500">
            <a href="/privacy" className="hover:underline hover:text-slate-800" data-testid="login-privacy-link">Privacy Policy</a>
            <span className="mx-2">·</span>
            <a href="/terms" className="hover:underline hover:text-slate-800" data-testid="login-terms-link">Terms of Use</a>
          </div>
        </div>
      </div>
    </div>
  );
}
